import type { ComponentProps, MutableRefObject, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  DisplayProps,
  FlexProps,
  HeightProps,
  OverflowProps,
  PositionProps,
  ResponsiveValue,
  WidthProps,
} from 'styled-system';
import { display, flex, height, overflow, position, width } from 'styled-system';

import { useIsObserved } from 'common/hooks/intersection-observer';
import type {
  AsProps,
  BoxSizingProps,
  MaskImageProps,
  TransitionProps,
  VisibilityProps,
} from '../../theme/system-utilities';
import { boxSizing, maskImage, transition } from '../../theme/system-utilities';
import type { RenderTitleProps } from '../../types/accordion';
import BorderBottom from './Border/BorderBottom';
import BorderTop from './Border/BorderTop';
import { useRenderContent } from './hooks';
import SectionButton from './SectionButton';
import Shadow from './Shadow';
import { DEFAULT_RENDER_TITLE } from './Title/utils';
import type { IconStylesProps, SectionBtnStylesProps } from './types';

const ACCORDION_TRANSITION_DURATION = 300;
const SECTION_POSITION = ['-16px', '-24px', '-48px', '-64px'];

interface AccordionSectionProps extends AsProps {
  title: string | ReactNode;
  sectionName: string;
  isExpanded: boolean;
  onTitleClick: (sectionName: string) => void;
  children: ReactNode[] | ReactNode;
  displayBorder?: string | Array<string | null>;
  displayTitle?: string | Array<string | null>;
  renderTitle?: (props: RenderTitleProps) => ReactNode;
  isCollapsible?: boolean;
  hasTopBorder?: boolean;
  hasBottomBorder?: boolean;
  keepContentWhenCollapsed?: boolean;
  scrollIntoView?: boolean;
  positionX?: ResponsiveValue<number | string>;
  onObserved?: (sectionName: string) => void;
  isAnimated?: boolean;
  contentHeight?: Nullable<number>;
  withHover?: boolean;
  showMore?: boolean;
  withTopBorderRadius?: boolean;
  withBottomBorderRadius?: boolean;
  iconStyles?: IconStylesProps;
  sectionBtnStyles?: SectionBtnStylesProps;
  keepBorderWhenExpanded?: boolean;
  headerButtonRef?: MutableRefObject<Nullable<HTMLButtonElement>>;
}

const Container = styled.div<FlexProps & WidthProps & BoxSizingProps & PositionProps>`
  ${boxSizing};
  ${flex};
  ${position};
  ${width};
`;

const containerStyles: ComponentProps<typeof Container> = {
  boxSizing: 'border-box',
  flex: 'none',
  position: 'relative',
  width: 1,
};

const ContentWrapper = styled.div<
  DisplayProps & HeightProps & OverflowProps & TransitionProps & MaskImageProps
>`
  ${display};
  ${height};
  ${overflow};
  ${transition};
  ${maskImage};
`;

const contentWrapperStyles: ComponentProps<typeof ContentWrapper> = {
  transition: `height ${ACCORDION_TRANSITION_DURATION}ms`,
};

const Content = styled.div<
  BoxSizingProps &
    DisplayProps &
    FlexProps &
    PositionProps &
    VisibilityProps &
    WidthProps &
    TransitionProps &
    Partial<Pick<AccordionSectionProps, 'isExpanded'>>
>`
  ${boxSizing};
  ${display};
  ${flex};
  ${position};
  ${transition};
  ${width};

  visibility: ${({ isExpanded }) => (isExpanded ? 'visible' : 'hidden')};
`;

const contentStyles: ComponentProps<typeof Content> = {
  position: 'relative',
  flex: 'none',
  width: 1,
  boxSizing: 'border-box',
  transition: `visibility ${ACCORDION_TRANSITION_DURATION}ms`,
};

// eslint-disable-next-line max-lines-per-function, complexity
const AccordionSection = ({
  isCollapsible = false,
  children,
  title,
  isExpanded,
  onTitleClick,
  hasTopBorder,
  keepBorderWhenExpanded,
  hasBottomBorder = true,
  sectionName,
  renderTitle = DEFAULT_RENDER_TITLE,
  displayBorder,
  displayTitle,
  keepContentWhenCollapsed,
  scrollIntoView,
  positionX = SECTION_POSITION,
  onObserved,
  isAnimated = false,
  contentHeight,
  withHover,
  showMore,
  withTopBorderRadius,
  withBottomBorderRadius,
  sectionBtnStyles,
  iconStyles,
  headerButtonRef,
  ...props
}: AccordionSectionProps) => {
  const renderContent = useRenderContent({
    isExpanded,
    keepContentWhenCollapsed,
  });
  const sectionContainerId = `accordion-section-container-${sectionName}`;
  const sectionTitleId = `accordion-section-title-${sectionName}`;
  const sectionRegionId = `accordion-section-region-${sectionName}`;
  const containerRef = useRef<Nullable<HTMLElement>>(null);
  const borderProps = {
    display: displayBorder,
    left: positionX,
    right: positionX,
  };
  const [observerRef, isObserved] = useIsObserved();
  const setElementRef = (element: HTMLDivElement) => {
    containerRef.current = element;
    observerRef(element);
  };

  useEffect(() => {
    if (scrollIntoView) {
      containerRef.current?.scrollIntoView();
    }
  }, [scrollIntoView]);

  useEffect(() => {
    if (isObserved && !!onObserved) {
      onObserved(sectionName);
    }
  }, [isObserved]);

  const contentWrapperUpdatedStyles = isAnimated
    ? { height: isExpanded ? 'auto' : 0, style: { height: contentHeight } }
    : { display: isExpanded ? 'block' : 'none' };

  const expandedBottom = {
    ...borderProps,
  };

  return (
    <Container
      id={sectionContainerId}
      data-section-name={sectionName}
      data-testid="accordion-section"
      ref={setElementRef}
      {...containerStyles}
      {...props}
    >
      {renderTitle({
        'data-testid': 'accordion-title',
        onClick: () => isCollapsible && onTitleClick(sectionName),
        active: isCollapsible,
        children: (
          <>
            {hasTopBorder && <BorderTop {...borderProps} />}
            {isCollapsible ? (
              <SectionButton
                sectionTitleId={sectionTitleId}
                sectionRegionId={sectionRegionId}
                isExpanded={isExpanded}
                keepBorderWhenExpanded={keepBorderWhenExpanded}
                hasBottomBorder={hasBottomBorder}
                withHover={withHover}
                withTopBorderRadius={withTopBorderRadius}
                withBottomBorderRadius={withBottomBorderRadius}
                iconStyles={iconStyles}
                sectionBtnStyles={sectionBtnStyles}
                ref={headerButtonRef}
              >
                {title}
              </SectionButton>
            ) : (
              <span id={sectionTitleId}>{title}</span>
            )}
            {hasBottomBorder && !withHover && <BorderBottom {...borderProps} />}
          </>
        ),
        ...(displayTitle && { display: displayTitle }),
      })}
      {(renderContent || isAnimated) && (
        <ContentWrapper
          {...contentWrapperStyles}
          {...contentWrapperUpdatedStyles}
          {...(isAnimated ? { overflow: 'hidden' } : {})}
          data-testid="accordion-section-content-wrapper"
        >
          <Content
            {...contentStyles}
            aria-labelledby={sectionTitleId}
            id={sectionRegionId}
            isExpanded={isExpanded}
            data-testid="accordion-section-content"
            role="region"
          >
            {children}
          </Content>
          {showMore && isExpanded && <Shadow />}
          {hasBottomBorder && !withHover && <BorderBottom {...expandedBottom} />}
        </ContentWrapper>
      )}
    </Container>
  );
};

export default AccordionSection;
